import { User } from "@repo/icons";
import { type FragmentType, graphql, useFragment } from "@repo/graphql-types";
import { InfoSectionV2 } from "@/components/info-section-v2";
import { PatientPaymentMethodInfo } from "@/components/patient-payment-method-info";

export const PatientPaymentsRefundPayeeSectionFragment = graphql(`
  fragment PatientPaymentsRefundPayeeSectionFragment on ExpensumGetRefundReceiptOutput {
    receipt {
      name
      card {
        brand
        lastFourDigits
      }
      bankAccount {
        bankName
      }
      installments
      paymentMethod
    }
  }
`);

interface PatientPaymentsRefundPayeeSectionProps {
  data: FragmentType<typeof PatientPaymentsRefundPayeeSectionFragment>;
}

export const PatientPaymentsRefundPayeeSection = ({
  data,
}: PatientPaymentsRefundPayeeSectionProps): JSX.Element => {
  const refundData = useFragment(PatientPaymentsRefundPayeeSectionFragment, data);
  const refundReceipt = refundData.receipt;

  const buildPaymentMethodInformation = (): JSX.Element | undefined => {
    if (!refundReceipt?.paymentMethod) return undefined;

    return (
      <PatientPaymentMethodInfo
        paymentMethod={
          refundReceipt.paymentMethod === "credit_card"
            ? "credito"
            : refundReceipt.paymentMethod
        }
        installments={refundReceipt.installments}
        brand={refundReceipt.card?.brand}
      />
    );
  };

  const recipientSection = {
    Icon: User,
    title: "Dados do favorecido",
    fields: (() => {
      if (!refundReceipt?.paymentMethod) return [];
      if (refundReceipt.paymentMethod === "pix") {
        return [
          { label: "Nome do titular", value: refundReceipt.name },
          { label: "Instituição", value: refundReceipt.bankAccount?.bankName },
          { label: "Forma de Pagamento", value: buildPaymentMethodInformation() },
        ];
      }
      if (
        refundReceipt.paymentMethod === "credit_card" ||
        refundReceipt.paymentMethod === "debit_card"
      ) {
        return [
          { label: "Nome do titular", value: refundReceipt.name },
          { label: "Forma de Pagamento", value: buildPaymentMethodInformation() },
          {
            label: "Cartão utilizado",
            value: refundReceipt.card?.lastFourDigits
              ? `**** **** **** ${refundReceipt.card.lastFourDigits}`
              : undefined,
          },
          { label: "Bandeira", value: refundReceipt.card?.brand },
        ];
      }
      // Default: render nome do titular e forma de pagamento
      return [
        { label: "Nome do titular", value: refundReceipt.name },
        { label: "Forma de Pagamento", value: buildPaymentMethodInformation() },
      ];
    })(),
  };

  return <InfoSectionV2 section={recipientSection} />;
};
